import {
  forwardRef,
  useCallback,
  useState,
  type CSSProperties,
  type ReactNode,
  type RefObject,
} from "react";
import { createPortal } from "react-dom";

import { Icon } from "@majourneys/icons";

import { popOrigin } from "./presence";
import {
  CONTROL_FILL,
  CONTROL_EDGE,
  GOLD_INK,
  GOLD_WASH,
  HAIRLINE,
  MUTED,
  NAVY,
  ROW_ACTIVE,
  controlStyle,
  menuPanelStyle,
  SHEET_BOTTOM,
  SHEET_INSET,
  sheetPanelStyle,
} from "./tokens";

/**
 * The pieces every menu in the kit is built from.
 *
 * All styling is inline, and the only class names are the four the injected
 * stylesheet owns (`mj-ui-pop`, `mj-ui-sheet`, `mj-ui-scroll`, `mj-ui-row`,
 * `mj-ui-press`). No Tailwind: the app's build does not scan packages, so a
 * utility written here would be emitted only by accident.
 */

/* ------------------------------------------------------------------ */
/* Trigger                                                             */
/* ------------------------------------------------------------------ */

/** What a caller needs to draw its own trigger and keep the menu working. */
export interface MenuTriggerRenderProps {
  readonly ref: RefObject<HTMLButtonElement | null>;
  readonly open: boolean;
  /** True once a choice has been made. */
  readonly chosen: boolean;
  /** The label the default trigger would have shown. */
  readonly label: string;
  readonly buttonProps: {
    readonly type: "button";
    readonly onClick: () => void;
    readonly "aria-haspopup": "listbox" | "menu" | "dialog";
    readonly "aria-expanded": boolean;
  };
}

/** The one moving part on a trigger: transform only, 160ms, out curve. */
export function Chevron({ open, size = 15 }: { open: boolean; size?: number }) {
  return (
    <Icon
      name="ri-arrow-down-s-line"
      size={size}
      aria-hidden="true"
      style={{
        flexShrink: 0,
        color: MUTED,
        transform: open ? "rotate(180deg)" : "none",
        transition: "transform 160ms cubic-bezier(0.23, 1, 0.32, 1)",
      }}
    />
  );
}

/**
 * The trigger a menu grows: white, one navy hairline, the hairline darkening
 * when the menu is open or a choice has been made. No band, no gradient. The
 * frame belongs to the panel that opens, not to the button.
 */
export const MenuTrigger = forwardRef<
  HTMLButtonElement,
  {
    open: boolean;
    chosen?: boolean;
    full?: boolean;
    size?: "sm" | "md";
    /** Sits above the value inside the control, for the form-field shape. */
    label?: string;
    value: ReactNode;
    lead?: ReactNode;
    onClick: () => void;
    ariaLabel?: string;
    ariaHasPopup?: "listbox" | "menu" | "dialog";
  }
>(function MenuTrigger(
  { open, chosen, full, size = "sm", label, value, lead, onClick, ariaLabel, ariaHasPopup = "listbox" },
  ref,
) {
  const small = size === "sm";
  return (
    <button
      ref={ref}
      type="button"
      onClick={onClick}
      aria-haspopup={ariaHasPopup}
      aria-expanded={open}
      aria-label={ariaLabel}
      className="mj-ui-press"
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: full ? "space-between" : undefined,
        width: full ? "100%" : undefined,
        gap: 8,
        minHeight: 36,
        borderRadius: 999,
        border: "none",
        cursor: "pointer",
        fontWeight: 600,
        fontSize: small ? 13 : 14,
        padding: small ? "6px 12px" : "10px 14px",
        ...controlStyle(!!(open || chosen)),
      }}
    >
      {lead}
      <span
        style={{
          display: "flex",
          minWidth: 0,
          flexDirection: "column",
          alignItems: "flex-start",
          textAlign: "start",
        }}
      >
        {label ? (
          <span
            style={{
              fontSize: 10.5,
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.07em",
              color: MUTED,
            }}
          >
            {label}
          </span>
        ) : null}
        <span style={WRAP}>{value}</span>
      </span>
      <Chevron open={open} />
    </button>
  );
});

/* Labels wrap instead of ending in "…": in fr and de a chosen value or an
   option name runs about a third longer than in en and must stay readable. */
const WRAP: CSSProperties = {
  overflowWrap: "break-word",
  minWidth: 0,
  maxWidth: "100%",
};

/* ------------------------------------------------------------------ */
/* Surface                                                             */
/* ------------------------------------------------------------------ */

export interface MenuSurfaceProps {
  /** From `usePresence`: keeps the panel mounted through its exit. */
  readonly presenceRef: (el: HTMLElement | null) => void;
  /** Registered with `useMenu` so the portalled sheet counts as inside. */
  readonly surfaceRef: RefObject<HTMLElement | null>;
  readonly state: "open" | "closed";
  readonly instant?: boolean;
  readonly sheet: boolean;
  readonly side?: "start" | "end";
  readonly width?: number | string;
  readonly role?: "listbox" | "menu" | "dialog";
  readonly ariaLabel?: string;
  readonly ariaMultiselectable?: boolean;
  /** The sheet's own heading, so the choice has a name on a phone. */
  readonly title: string;
  readonly closeLabel: string;
  readonly onClose: () => void;
  readonly dir: "ltr" | "rtl";
  readonly children: ReactNode;
}

/**
 * One control, two bodies.
 *
 * Over the breakpoint it is a framed panel hanging off the corner of its
 * trigger. Under it, the list comes up from the bottom of the screen as a
 * card floating 10px in from the edges, with a grab handle, a title, and a scrim over what is behind. Twelve rows
 * hanging off a 22px heading on a 375px phone is what that replaces.
 *
 * The sheet is portalled to `<body>`. The Discover sheet it opens from carries
 * a `transform`, and a transformed element becomes the containing block for
 * anything `position: fixed` inside it, so an in-place sheet would have been
 * pinned to the bottom of the drawer instead of the bottom of the screen.
 */
export function MenuSurface({
  presenceRef,
  surfaceRef,
  state,
  instant,
  sheet,
  side = "end",
  width,
  role = "listbox",
  ariaLabel,
  ariaMultiselectable,
  title,
  closeLabel,
  onClose,
  dir,
  children,
}: MenuSurfaceProps) {
  const setSurface = useCallback(
    (el: HTMLElement | null) => {
      presenceRef(el);
      surfaceRef.current = el;
    },
    [presenceRef, surfaceRef],
  );

  if (!sheet) {
    return (
      <div
        ref={setSurface}
        role={role}
        aria-label={ariaLabel}
        aria-multiselectable={ariaMultiselectable}
        data-state={state}
        data-instant={instant ? "true" : undefined}
        className="mj-ui-pop"
        style={{
          position: "absolute",
          zIndex: 60,
          top: "calc(100% + 7px)",
          insetInlineEnd: side === "end" ? 0 : undefined,
          insetInlineStart: side === "start" ? 0 : undefined,
          width,
          // Never wider than the screen it hangs in.
          maxWidth: "calc(100vw - 32px)",
          padding: 6,
          boxSizing: "border-box",
          transformOrigin: popOrigin(side, dir),
          ...menuPanelStyle(18),
        }}
      >
        {children}
      </div>
    );
  }

  if (typeof document === "undefined") return null;

  return createPortal(
    <>
      <div
        className="mj-ui-scrim"
        data-state={state}
        aria-hidden="true"
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 2147483000,
          background: "rgba(17,14,57,0.34)",
        }}
      />
      <div
        ref={setSurface}
        role={role}
        aria-label={ariaLabel}
        aria-multiselectable={ariaMultiselectable}
        data-state={state}
        className="mj-ui-sheet"
        style={{
          position: "fixed",
          insetInline: SHEET_INSET,
          bottom: SHEET_BOTTOM,
          zIndex: 2147483001,
          padding: "6px 6px 10px",
          boxSizing: "border-box",
          ...sheetPanelStyle(26),
        }}
      >
        <div
          aria-hidden="true"
          style={{
            width: 36,
            height: 4,
            margin: "4px auto 8px",
            borderRadius: 999,
            background: "rgba(17,14,57,0.18)",
          }}
        />
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 8,
            padding: "0 8px 4px",
          }}
        >
          <p style={{ margin: 0, fontSize: 15, fontWeight: 700, color: NAVY }}>{title}</p>
          <button
            type="button"
            onClick={onClose}
            aria-label={closeLabel}
            className="mj-ui-press"
            style={{
              display: "grid",
              placeItems: "center",
              width: 28,
              height: 28,
              borderRadius: 999,
              border: "none",
              cursor: "pointer",
              background: "rgba(17,14,57,0.06)",
              color: NAVY,
            }}
          >
            <Icon name="ri-close-line" size={15} aria-hidden="true" />
          </button>
        </div>
        {children}
      </div>
    </>,
    document.body,
  );
}

/* ------------------------------------------------------------------ */
/* Body                                                                */
/* ------------------------------------------------------------------ */

/**
 * The scrolling body of a menu. A long list scrolls up and down, which is fine;
 * nothing ever moves sideways, and the bar itself never shows.
 */
export function MenuScroll({
  listRef,
  max = 264,
  role,
  ariaLabel,
  ariaMultiselectable,
  children,
}: {
  listRef?: RefObject<HTMLDivElement | null>;
  max?: number;
  /** Set when the surface around it is a dialog, so the rows still have a list
   *  to belong to. Rows with `role="option"` and no listbox above them are rows
   *  a screen reader cannot count. */
  role?: "listbox" | "menu";
  ariaLabel?: string;
  ariaMultiselectable?: boolean;
  children: ReactNode;
}) {
  return (
    <div
      ref={listRef}
      role={role}
      aria-label={ariaLabel}
      aria-multiselectable={ariaMultiselectable}
      className="mj-ui-scroll"
      style={{
        position: "relative",
        maxHeight: max,
        overflowY: "auto",
        overflowX: "hidden",
        overscrollBehavior: "contain",
      }}
    >
      {children}
    </div>
  );
}

/**
 * One choice. The highlight and the selection are two different things and are
 * drawn differently on purpose: the highlight is where the arrow keys are
 * standing, the gold tick is what has actually been chosen.
 *
 * `data-active` is written into the DOM because a background colour cannot be
 * asserted on, and the highlight is the half of a menu that ships broken.
 */
export function OptionRow({
  index,
  active,
  selected,
  disabled,
  lead,
  title,
  meta,
  trailing,
  onHover,
  onClick,
  size = "md",
  role = "option",
}: {
  index: number;
  active: boolean;
  selected?: boolean;
  disabled?: boolean;
  lead?: ReactNode;
  title: ReactNode;
  meta?: ReactNode;
  trailing?: ReactNode;
  onHover?: () => void;
  onClick?: () => void;
  size?: "md" | "lg";
  role?: "option" | "menuitem";
}) {
  const large = size === "lg";
  return (
    <button
      type="button"
      role={role}
      data-row={index}
      data-active={active ? "true" : undefined}
      aria-selected={role === "option" ? !!selected : undefined}
      disabled={disabled}
      onMouseEnter={onHover}
      onClick={onClick}
      className="mj-ui-row mj-ui-press"
      style={{
        display: "flex",
        width: "100%",
        alignItems: "center",
        gap: 10,
        borderRadius: 12,
        border: "none",
        cursor: disabled ? "default" : "pointer",
        opacity: disabled ? 0.4 : 1,
        textAlign: "start",
        minHeight: large ? 46 : 38,
        padding: large ? "10px 12px" : "8px 10px",
        background: active ? ROW_ACTIVE : "transparent",
        transition: "background-color 120ms linear",
      }}
    >
      {lead}
      <span style={{ display: "flex", minWidth: 0, flex: 1, flexDirection: "column" }}>
        <span
          style={{
            ...WRAP,
            color: NAVY,
            fontSize: large ? 14.5 : 13.5,
            fontWeight: selected ? 700 : 500,
          }}
        >
          {title}
        </span>
        {meta ? (
          <span style={{ ...WRAP, fontSize: 11.5, color: MUTED }}>{meta}</span>
        ) : null}
      </span>
      {trailing}
      {selected ? (
        <Icon name="ri-check-line" size={15} aria-hidden="true" style={{ color: GOLD_INK, flexShrink: 0 }} />
      ) : null}
    </button>
  );
}

/** The count on the end of a row: how many spots a choice leads to. */
export function CountTag({ children }: { children: ReactNode }) {
  return (
    <span
      style={{
        flexShrink: 0,
        fontSize: 11.5,
        fontWeight: 700,
        fontVariantNumeric: "tabular-nums",
        color: MUTED,
      }}
    >
      {children}
    </span>
  );
}

/** A round well for a leading glyph, so an icon can centre itself. */
export function Well({ children, tone = "ink" }: { children: ReactNode; tone?: "ink" | "gold" }) {
  return (
    <span
      aria-hidden="true"
      style={{
        display: "grid",
        placeItems: "center",
        width: 28,
        height: 28,
        flexShrink: 0,
        borderRadius: 999,
        background: tone === "gold" ? GOLD_WASH : "rgba(17,14,57,0.06)",
        color: tone === "gold" ? GOLD_INK : NAVY,
      }}
    >
      {children}
    </span>
  );
}

/** Nothing matched, or nothing to show yet. */
export function EmptyRow({ title, hint }: { title: string; hint?: string }) {
  return (
    <div style={{ padding: "28px 16px", textAlign: "center" }}>
      <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: NAVY }}>{title}</p>
      {hint ? (
        <p style={{ margin: "4px 0 0", fontSize: 12, lineHeight: 1.4, color: MUTED }}>{hint}</p>
      ) : null}
    </div>
  );
}

/** The hairline between two groups of rows inside one menu. */
export function MenuRule() {
  return <div aria-hidden="true" style={{ height: 1, margin: "4px 0", background: HAIRLINE }} />;
}

/** The small caps a menu labels a group of rows with. */
export function MenuLabel({ children }: { children: ReactNode }) {
  return (
    <p
      style={{
        margin: 0,
        padding: "8px 10px 4px",
        fontSize: 10.5,
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "0.08em",
        color: MUTED,
      }}
    >
      {children}
    </p>
  );
}

/**
 * The field a long list gets. Pinned at the top of the panel and given focus as
 * the panel lands, so twelve cities, or a hundred, are three keystrokes away
 * instead of a scroll.
 */
export function MenuSearchField({
  inputRef,
  value,
  onChange,
  placeholder,
  clearLabel,
}: {
  inputRef: RefObject<HTMLInputElement | null>;
  value: string;
  onChange: (next: string) => void;
  placeholder: string;
  clearLabel: string;
}) {
  const [focused, setFocused] = useState(false);
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        marginBottom: 4,
        padding: "0 12px",
        borderRadius: 13,
        background: CONTROL_FILL,
        boxShadow: `0 0 0 1.5px ${focused ? NAVY : CONTROL_EDGE}`,
        transition: "box-shadow 160ms cubic-bezier(0.23, 1, 0.32, 1)",
      }}
    >
      <Icon name="ri-search-line" size={15} aria-hidden="true" style={{ color: MUTED, flexShrink: 0 }} />
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        placeholder={placeholder}
        aria-label={placeholder}
        style={{
          minWidth: 0,
          flex: 1,
          background: "transparent",
          border: "none",
          outline: "none",
          padding: "10px 0",
          fontSize: 13.5,
          color: NAVY,
        }}
      />
      {value ? (
        <button
          type="button"
          onClick={() => {
            onChange("");
            inputRef.current?.focus();
          }}
          aria-label={clearLabel}
          className="mj-ui-press"
          style={{
            display: "grid",
            placeItems: "center",
            width: 24,
            height: 24,
            flexShrink: 0,
            borderRadius: 999,
            border: "none",
            cursor: "pointer",
            background: "rgba(17,14,57,0.07)",
            color: NAVY,
          }}
        >
          <Icon name="ri-close-line" size={12} aria-hidden="true" />
        </button>
      ) : null}
    </div>
  );
}
